'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { Text, Tooltip, Skeleton } from '@terra/ui-design-system';
import type { TerraIcon } from '@terra/ui-icons';
import { HelpCircleIcon } from '@terra/ui-icons';
import type { ReactNode } from 'react';

type DashboardWidgetProps = {
  title: string;
  subtitle?: string;
  tooltip?: string;
  icon?: TerraIcon;
  children: ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  contentHeight?: number;
  onClick?: () => void;
};

export function DashboardWidget({
  title,
  subtitle,
  tooltip,
  icon: Icon,
  children,
  isLoading,
  isEmpty,
  contentHeight,
  onClick,
}: DashboardWidgetProps) {
  return (
    <Box
      w="full"
      h="full"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      bg="bg.default"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      _hover={onClick ? { borderColor: 'border.default' } : {}}
      transition="border-color 0.2s"
    >
      {/* Header */}
      <Box px="4" py="3" borderBottomWidth="1px" borderColor="border.subtle">
        <HStack gap="2" alignItems="center">
          {Icon && <Icon size={16} color="fg.muted" />}
          <Text fontSize="sm" fontWeight="semibold" color="fg.default">
            {title}
          </Text>
          {tooltip && (
            <Tooltip title={tooltip}>
              <HelpCircleIcon size={14} color="fg.muted" />
            </Tooltip>
          )}
          {subtitle && (
            <Text fontSize="xs" color="fg.muted" ml="auto">
              {subtitle}
            </Text>
          )}
        </HStack>
      </Box>
      {/* Content */}
      <Box p="4" minH={contentHeight}>
        {isLoading ? (
          <Skeleton h={contentHeight || 100} w="full" />
        ) : isEmpty ? (
          <Text color="fg.muted" fontSize="sm">
            No data available
          </Text>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}
